import { ref, type Ref } from 'vue';
import { cloneDeep } from 'lodash';
import Swal from 'sweetalert2';

import { useCustomer } from '@/use/customer';
import type { CustomLocationType } from '@/models/address';
import type { ScheduledGroceryEstimate } from '@/models/scheduledGrocery';

const scheduledGroceryDefaultData = {
  callerId: '',
  streetAddress: '',
  streetAddress2: '',
  state: '',
  city: '',
  zip: '',
  country: '',
  latitude: null,
  longitude: null,
  store: null,
  storeItems: [],
  isMultiple: false,
  scheduleDate: null,
  scheduleTime: null,
  repeatEveryXWeek: 1,
  day0: false,
  day1: false,
  day2: false,
  day3: false,
  day4: false,
  day5: false,
  day6: false,
  endOn: null,
  endOnType: 'never',
  endAfterXTimes: 0,
  noteToShopper: '',
  isLeaveAtDoor: false,
  tip: 0,
};

const scheduledGroceryData: Ref<any> = ref(cloneDeep(scheduledGroceryDefaultData));
const scheduledGroceryStep = ref(0);
const scheduledGroceryStatus: Ref<any> = ref({
  isLoading: false,
  loadingMessage: '',
});
const scheduledGroceryEstimate: Ref<ScheduledGroceryEstimate | null> = ref(null);
const scheduledGroceryId: Ref<any> = ref(null);
const groceryOrderId: Ref<any> = ref(null);
const savedGroceryId: Ref<any> = ref(null);
const groceryAddress: Ref<CustomLocationType> = ref({
  streetAddress: '',
  state: '',
  city: '',
  zip: '',
  latitude: null,
  longitude: null,
  googlePlaceId: null,
});
export const useGrocery = () => {
  const { customerInfo, isNeedToShowFirstGroceryPopup, isShownFirstGroceryPopup, setShownFirstGroceryPopup } = useCustomer();

  const showFirstGroceryPopup = () => {
    if (!customerInfo.value?.id || isShownFirstGroceryPopup.value || !isNeedToShowFirstGroceryPopup.value) {
      return;
    }

    Swal.fire({
      title: 'First Time Grocery Customer',
      text: "This is the customer's first grocery order. Please explain the grocery ordering process and provide appropriate first-time service.",
      imageUrl: '/static/img/first_time_delivery.png',
      imageWidth: 200,
      imageHeight: 200,
      imageAlt: 'First Time Grocery',
    });

    setShownFirstGroceryPopup(true);
  };

  const setScheduledGroceryData = (value: any) => {
    scheduledGroceryData.value = cloneDeep(value);
  };

  const setScheduledGroceryStep = (step: number) => {
    scheduledGroceryStep.value = step;
  };

  const setScheduledGroceryStatus = (value: any) => {
    scheduledGroceryStatus.value = value;
  };

  const setScheduledGroceryId = (value: any) => {
    scheduledGroceryId.value = value;
  };

  const resetScheduledGroceryData = () => {
    scheduledGroceryData.value = cloneDeep(scheduledGroceryDefaultData);
    scheduledGroceryStep.value = 0;
    scheduledGroceryStatus.value = {
      isLoading: false,
      loadingMessage: '',
    };
    scheduledGroceryEstimate.value = null;
  };

  const setGroceryOrderId = (value: any) => {
    groceryOrderId.value = value;
  };

  const setSavedGroceryId = (value: any) => {
    savedGroceryId.value = value;
  };

  const setGroceryAddress = (address: any) => {
    groceryAddress.value = {
      streetAddress: address?.streetAddress || '',
      state: address?.state || '',
      city: address?.city || '',
      zip: address?.zip || '',
      latitude: address?.latitude ? Number(address.latitude) : null,
      longitude: address?.longitude ? Number(address.longitude) : null,
      noteToDriver: '',
      gourmetNoteToDriver: '',
      googlePlaceId: address?.googlePlaceId,
    };
  };

  return {
    scheduledGroceryData,
    scheduledGroceryStep,
    scheduledGroceryStatus,
    scheduledGroceryEstimate,
    scheduledGroceryId,
    groceryOrderId,
    savedGroceryId,
    groceryAddress,

    showFirstGroceryPopup,
    setScheduledGroceryId,
    setScheduledGroceryStatus,
    setScheduledGroceryData,
    setScheduledGroceryStep,
    resetScheduledGroceryData,
    setGroceryOrderId,
    setSavedGroceryId,
    setGroceryAddress,
  };
};
